import React, { useEffect, useState } from "react";
import { X, ShieldCheck, Lock, Loader2 } from "lucide-react";
import { load } from "@cashfreepayments/cashfree-js";
import { api, formatINR } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

export default function QuickOrderModal({ product, qty, open, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    customer_name: "", email: "", phone: "",
    address: "", city: "", state: "", pincode: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const total = product ? Number(product.price) * qty : 0;
  const shipping = total > 999 ? 0 : 99;
  const grand = total + shipping;

  useEffect(() => {
    if (open && user) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || user.name || "",
        email: f.email || user.email || "",
        phone: f.phone || user.phone || "",
      }));
    }
  }, [open, user]);

  useEffect(() => {
    if (!open) return;
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, [open]);

  if (!open || !product) return null;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/orders/quick", {
        product_id: product.id, quantity: qty,
        ...form,
      });
      const { payment_session_id, mode } = r.data;
      const cashfree = await load({ mode: mode === "production" ? "production" : "sandbox" });
      if (!cashfree) throw new Error("Payment gateway failed to load. Please try again.");
      toast.success("Order reserved! Redirecting to secure Cashfree checkout…");
      await cashfree.checkout({
        paymentSessionId: payment_session_id,
        redirectTarget: "_self",
      });
      // Browser navigates away to Cashfree's hosted page — nothing else to do here.
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Could not place order. Try again.";
      toast.error(String(detail));
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="quick-order-modal"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="quick-order-close"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-white/60 hover:text-white z-10"
          aria-label="Close"
        ><X size={20} /></button>

        <div className="p-6 sm:p-8">
          <div className="overline text-[#D96A1B] mb-2">Instant Pay</div>
          <h2 className="font-display text-2xl sm:text-3xl uppercase tracking-tighter mb-2">Complete Your Order</h2>
          <p className="text-sm font-body text-white/60 mb-6">We'll redirect you to Cashfree's secure checkout right after this. Fill in your ship-to details so we know where to send your gear.</p>

          <div className="flex gap-3 border border-white/10 p-3 mb-6">
            <img src={(product.images || [])[0]} alt="" className="w-14 h-16 object-cover" />
            <div className="flex-1 min-w-0">
              <div className="font-display uppercase text-xs sm:text-sm tracking-tight line-clamp-1">{product.name}</div>
              <div className="text-[11px] text-white/50 font-body mt-0.5">Qty {qty} · {product.subcategory}</div>
            </div>
            <div className="text-right">
              <div className="font-display text-[#D96A1B]">{formatINR(grand)}</div>
              <div className="text-[10px] text-white/40 font-body">incl. {shipping === 0 ? "free ship" : formatINR(shipping) + " ship"}</div>
            </div>
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="qo-name" required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Full Name" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
              <input data-testid="qo-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (10 digits)" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
            </div>
            <input data-testid="qo-email" required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
            <input data-testid="qo-address" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street Address (House/Flat No, Building, Area)" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
            <div className="grid sm:grid-cols-3 gap-3">
              <input data-testid="qo-city" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
              <input data-testid="qo-state" required value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="State" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
              <input data-testid="qo-pincode" required value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} placeholder="Pincode" className="bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />
            </div>
            <textarea data-testid="qo-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows="2" placeholder="Order notes (optional) — size, colour preference, gift note" className="w-full bg-[#111] border border-white/15 px-4 py-3 focus:outline-none focus:border-[#D96A1B] font-body text-sm" />

            <button data-testid="qo-submit" disabled={loading} className="btn-orange w-full justify-center py-3">
              {loading ? (<><Loader2 size={16} className="animate-spin" /> Redirecting to Cashfree…</>) : (<>Pay Securely — {formatINR(grand)} <Lock size={14} /></>)}
            </button>
            <p className="text-[11px] font-body text-white/50 text-center flex items-center justify-center gap-1.5">
              <ShieldCheck size={11} className="text-[#D96A1B]" /> Your order is reserved for 24 hours. Payments are processed by Cashfree — UPI, Cards & Netbanking.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
